import { LinkOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GenericDialog from 'switch-fe-shared/components/Dialogs/GenericDialog';
import { PRODUCTS } from 'switch-shared/types/general.enums';

import { useLocalStorage } from '../../hooks/useStorage';
import ReleaseNoteContent from '../../pages/ReleaseNotes/ReleaseNoteContent';
import pages from '../../router/routes';
import { useSearchReleaseNotesQuery } from '../../services/switch-rest-api/release-note.service';

// Only auto-surface releases published recently, so a brand-new user with empty
// localStorage isn't shown a stale release the first time they ever log in.
const RECENT_RELEASE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

const SEEN_VERSION_KEY = 'whatsNew/seenReleaseVersion';

/**
 * Auto-opens once per browser when a new admin-console release note is
 * published, showing the latest note inline with a link to the full page.
 * Renders nothing unless there is an unseen release published in the last two
 * weeks. Mounted inside the authenticated SidebarLayout.
 */
const WhatsNewDialog = () => {
  const navigate = useNavigate();

  const { data } = useSearchReleaseNotesQuery({
    target: PRODUCTS.ADMIN_CONSOLE,
    limit: '1',
  });
  const latestNote = data?.data[0];

  const [seenVersion, setSeenVersion] = useLocalStorage<string | null>(
    SEEN_VERSION_KEY,
    null
  );

  const isRecent = latestNote
    ? Date.now() - new Date(latestNote.createdAt).getTime() <=
      RECENT_RELEASE_WINDOW_MS
    : false;

  const open = Boolean(
    latestNote && isRecent && latestNote.releaseVersion !== seenVersion
  );

  if (!open || !latestNote) return null;

  const dismiss = () => setSeenVersion(latestNote.releaseVersion);

  const viewAll = () => {
    dismiss();
    navigate(pages.releaseNotes.path);
  };

  return (
    <GenericDialog
      open={open}
      title={`What's New — ${latestNote.releaseVersion}!`}
      maxWidth='md'
      fullWidth
      onConfirm={dismiss}
      confirmButtonText='Got it'
      onCancel={viewAll}
      cancelButtonText='View all release notes'
      cancelEndIcon={<LinkOutlined />}
      onClose={dismiss}
      customContent={
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <ReleaseNoteContent content={latestNote.content} />
        </Box>
      }
    />
  );
};

export default WhatsNewDialog;
